import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useForm, UseFormReturn } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  AccessCheckoutForm,
  AccessCheckoutFormSchema,
} from 'api/accessexpressv3/schemas/AccessCheckoutSchemas';
import { useMutation } from 'react-query';
import { submitOrder, updateOrder } from 'api/accessexpressv3/api';
import showToast from 'components/Toast/Toast';
import { useLocation, useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { CartSiteAddress, useCartContext } from '../CartProvider';
import { useRouteChange } from 'hooks/useRouteChange';
import { buildItemPayload } from '../utils';

interface AccessCheckoutContextType {
  currentIndex: number;
  locations: CartSiteAddress[];
  form: UseFormReturn<AccessCheckoutForm>;
  goToNext: () => Promise<void>;
  goToPrevious: () => void;
  isCheckoutFormValid: () => Promise<boolean>;
  onSubmit: (
    data: AccessCheckoutForm & { invoiceId?: string },
  ) => Promise<void>;
  isCheckoutLoading: boolean;
  resetForm: () => void;
}

const AccessCheckoutContext = createContext<AccessCheckoutContextType | null>(
  null,
);

export const AccessCheckoutProvider = observer(
  ({ children }: { children: React.ReactNode }) => {
    const cart = useCartContext();
    const navigate = useNavigate();
    const location = useLocation();

    const locationAssignmentPending = useMemo(
      () => location.state?.locationAssignmentPending,
      [location.state],
    );

    const cartLocations = useMemo(() => {
      const locations = cart.locations;

      if (
        (locations.length === 0 &&
          cart.items.every((item) => item?.type === 'Mobility')) ||
        locationAssignmentPending
      ) {
        return [
          ...locations,
          {
            address_line_1: '',
            address_line_2: null,
            street: null,
            city: '',
            state: '',
            zip: '',
            name: '',
            site_name: null,
            macnum: '',
            parent_macnum: '',
            id: undefined, // 'new'
            external_id: '',
          },
        ];
      }

      return locations;
    }, [cart.items, cart.locations, locationAssignmentPending]);

    const [currentIndex, setCurrentIndex] = useState(0);

    const form = useForm<AccessCheckoutForm>({
      resolver: zodResolver(AccessCheckoutFormSchema),
      defaultValues: {},
      mode: 'onChange',
    });
    const resetForm = useCallback(() => {
      if (!cart.loadingDraftForPricing) {
        if (cartLocations.length > 0) {
          form.reset({
            locations: cartLocations.map((loc) => {
              const itemsForLocation =
                loc.id !== undefined
                  ? cart.primaryItemsByLocation(loc.id)
                  : cart.unassignedPrimaryMobilityItems;

              const needsShippingForLocation = itemsForLocation.some(
                (item) => item.type === 'Mobility',
              );

              return {
                ...loc,
                id:
                  loc.id === undefined || loc.id === null
                    ? loc.id
                    : String(loc.id),
                needsShipping: needsShippingForLocation,

                business_name: '',
                local_contact_name: '',
                local_contact_phone: '',
                address_line_2: loc.address_line_2 || '',

                parent_account: undefined,
                location_name: '',
                billing_type: undefined,
                invoice_style: undefined,
                invoice_delivery_method: undefined,
                location_contact_email: '',

                speed: undefined,
              };
            }),
          });
        } else {
          form.reset({
            locations: [
              {
                address_line_1: '',
                id: undefined, // 'new'
                macnum: '',
                city: '',
                state: '',
                street: null,
                zip: '',

                needsShipping: cart.items.some(
                  (item) => item.type === 'Mobility',
                ),
                business_name: '',
                local_contact_name: '',
                local_contact_phone: '',
                parent_account: undefined,
                location_name: '',
                billing_type: undefined,
                invoice_style: undefined,
                invoice_delivery_method: undefined,
                location_contact_email: '',
                speed: undefined,
              },
            ],
          });
        }
      }
    }, [cart, cartLocations, form]);

    useRouteChange(() => {
      resetForm();
    });

    useEffect(() => {
      resetForm();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cart.loadingDraftForPricing]);

    useEffect(() => {
      setCurrentIndex(0);
    }, [location.pathname, cartLocations.length]);

    const {
      trigger,
      formState: { errors },
    } = form;

    const totalLocations = cartLocations.length;

    const goToPrevious = useCallback(() => {
      setCurrentIndex((prev) => Math.max(prev - 1, 0));
    }, []);
    const goToNext = useCallback(async () => {
      const isValid = await trigger(`locations.${currentIndex}`);
      if (!isValid) return;

      setCurrentIndex((prev) => Math.min(prev + 1, totalLocations - 1));
    }, [trigger, currentIndex, totalLocations]);

    const { mutate, isLoading } = useMutation(
      ({
        draftId,
        order_name,
        invoice_id,
      }: {
        draftId: string;
        order_name: string;
        invoice_id?: string;
      }) => submitOrder(draftId, { order_name, invoice_id }),
      {
        onSuccess: (data) => {
          showToast.confirmation({
            message: `New order #${data.order_number} placed successfully.`,
          });

          cart.reset();
          navigate('/access-express/v3', { replace: true });
        },
      },
    );

    const onSubmit = async (
      data: AccessCheckoutForm & { invoiceId?: string },
    ) => {
      const _locations = form.getValues('locations').map((location, idx) => {
        let items = (
          location.id === undefined
            ? cart.items.filter((item) => !item.location_id)
            : cart.itemsByLocation(location.id)
        ).map(buildItemPayload);

        if (form.getValues('locations').length === 1 && items.length === 0) {
          items = cart.items.map(buildItemPayload);
        }

        return {
          // TODO: Extract this logic into a reusable function
          address: {
            ...location,
            ...data.locations[idx],
            ...(!location.macnum
              ? {
                  parent_macnum: data.locations[idx].parent_account?.value,
                  parent_name: data.locations[idx].parent_account?.label,
                }
              : {}),
            billing_type: data.locations[idx].billing_type?.value ?? null,
            invoice_style: data.locations[idx].invoice_style?.value ?? null,
            invoice_delivery_method:
              data.locations[idx].invoice_delivery_method?.value ?? null,
            id: location.id as string,
          },
          order_items: items,
        };
      });

      if (cart.draftId) {
        await updateOrder(
          {
            additional_details: cart.additionalDetails ?? {},
            locations: _locations,
            order_name: data.order_name as string,
            invoice_id: data.invoiceId,
          },
          cart.draftId,
        );
        mutate({
          draftId: cart.draftId,
          order_name: data.order_name as string,
          invoice_id: data.invoiceId,
        });
      }
    };

    const isCheckoutFormValid = useCallback(async () => {
      const validationResults = await Promise.all(
        cartLocations.map(async (_, index) => {
          const valid = await trigger(`locations.${index}`);
          if (!valid) {
            // Here, you can collect errors for this location
            // Example: log all errors for this index
            const locErrors = errors.locations?.[index];
            if (locErrors) {
              Object.entries(locErrors).forEach(([field, err]) => {
                if (
                  typeof err === 'object' &&
                  err !== null &&
                  'message' in err &&
                  typeof (err as { message?: unknown }).message === 'string'
                ) {
                  // Show in UI, toast, or log
                  console.warn(
                    `Location ${index} field "${field}": ${
                      typeof err === 'object' &&
                      err !== null &&
                      'message' in err
                        ? (err as { message: string }).message
                        : ''
                    }`,
                  );
                }
              });
            }
          }
          return valid;
        }),
      );
      return validationResults.every(Boolean);
    }, [cartLocations, trigger, errors]);

    // const cartConflictProps = useCartConflictModal();

    return (
      <AccessCheckoutContext.Provider
        value={{
          form,
          locations: cartLocations,
          goToNext,
          goToPrevious,
          currentIndex,
          onSubmit,
          isCheckoutFormValid,
          isCheckoutLoading: isLoading,
          resetForm,
        }}
      >
        {children}
      </AccessCheckoutContext.Provider>
    );
  },
);

export const useAccessCheckoutContext = () => {
  const context = useContext(AccessCheckoutContext);
  if (!context) {
    throw new Error(
      'useAccessCheckoutContext must be used within a CheckoutProvider',
    );
  }
  return context;
};
